import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import moment from 'moment';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlusCircle, faCheck, faTrash } from '@fortawesome/free-solid-svg-icons';

import { Formik, Form } from 'formik';
import Grid from '@material-ui/core/Grid';
import Switch from '@material-ui/core/Switch';
import Typography from '@material-ui/core/Typography';
import FormControlLabel from '@material-ui/core/FormControlLabel';

import FileUploader from '../../../../components/FileUploader';
import ModalDrawerSwitcher from '../../../../components/ModalDrawerSwitcher';
import Modal from '../../../../components/Modal';
import Loader from '../../../../components/Loader';
import Input from '../../../../components/Input';
import Button from '../../../../components/Button';
import SingleDatePicker from '../../../../components/SingleDatePicker';
import Select from '../../../../components/Select';

import { paymentValidations, passwordValidations } from '../validations';
import { handleRegistration, handleConfirm, setRemove, clearChanges } from '../../store/actions';
import { setAttachment } from '../../store/actions';
import { loadConfigurations } from '../../../Operational/store/thunk';
import { upsertTransaction, removeTransaction, loadResume, upsertAttachment } from '../../store/thunk';
import { convertDateToDB, convertFormattedMoneyToNumber, convertFormattedMoneyAndNegativeMoneyToNumber, convertToBrl } from '../../../../helpers/converters';
import { getFileExtensionFromUrl } from '../../../../helpers/common';

import useWindowDimensions from 'helpers/hooks/useWindowDimensions';

import { SITE_RESTRICTIONS } from '../../../../helpers/restrictions';

export default function Payment({ isOpen, establishmentId }) {
  const dispatch = useDispatch();

  const { width } = useWindowDimensions();
  const isSm = width < 960;
  const isMobile = width < 728;

  const { userId } = useSelector(state => state.profile);
  const { establishmentId: filterEstablishmentId } = useSelector(state => state.businessInfo);
  const { startDate, endDate } = useSelector(state => state.dateFilter);
  const { qp, selects } = useSelector(state => state.filters);
  const { submit, isConfirm, isRemoving, isLoading } = useSelector(state => state.transactions);
  const page = useSelector(state => state.pagination.activePage);
  const pageSize = useSelector(state => state.pagination.size);
  const userRestrictions = useSelector(state => state.profile.userRestrictions);
  const { option } = useSelector(state => state.modalDrawerSwitcher);

  const isModal = option == 'modal';

  const {
    nfInfo: {
      rpsSeries,
      rpsNumber,
      nfNumber
    } = {}
  } = useSelector(state => state.provisionalReceipt);

  const { cashierId, transactionId, attachment } = submit;

  const [isAttachmentModalOpen, setIsAttachmentModalOpen] = useState(false);
  const [attachmentRemoveLoading, setAttachmentRemoveLoading] = useState(false);
  const [isAttachmentError, setIsAttachmentError] = useState(false);

  const [isInvalidAmount, setIsInvalidAmount] = useState(false);

  const availableAccounts = selects
    ?.find(select => select?.id == 'account')?.options
    ?.filter(account => +account?.accountTypeId != 1);

  const paymentCategories = selects
    ?.find(select => select?.id == 'paymentCategory')?.options
    ?.map(option => {
      return {
        value: option?.categoryId,
        label: option?.categoryName
      }
    })
    ?.filter(option => option?.value);

  const isOnlyOneAccountAvailable = availableAccounts
    ?.filter(account => !!account?.value)
    ?.length == 1;

  const getOnlyAccountAvailable = () => {
    const account = availableAccounts?.filter(account => !!account?.value)[0]?.value || '';
    return account;
  }

  useEffect(() => {
    if(establishmentId) {
      dispatch(loadConfigurations(userId, establishmentId));
    }
  }, [establishmentId]);

  const handleClosePayment = () => {
    dispatch(handleRegistration(false));
  }

  const handleUploadFile = e => {
    const receipt = e.target.files[0];

    dispatch(upsertAttachment(userId, establishmentId || filterEstablishmentId, receipt))
      .then(() => toast.warn('Salvar para persistir o comprovante!'))
      .catch(() => dispatch(setAttachment(null)));
  }

  const handleRemovePayment = data => {
    const extraProps = {
      userId,
      establishmentId,
      page,
      pageSize,
      startDate,
      endDate
    }

    const params = { transactionId, ...data };

    dispatch(removeTransaction(params, extraProps));
  }

  const handleSavePayment = (values, actions) => {
    const { resetForm } = actions;

    const extraProps = {
      userId,
      establishmentId: filterEstablishmentId,
      page,
      pageSize,
      startDate,
      endDate
    }

    const params = {
      isConfirm,
      ...values,
      previsionDateTime: moment(values.previsionDateTime).format('YYYY-MM-DD HH:mm:ss'),
      paymentDateTime: moment(values.paymentDateTime).format('YYYY-MM-DD HH:mm:ss'),
      attachment
    }

    if(+convertFormattedMoneyAndNegativeMoneyToNumber(params?.amount) == 0) {
      setIsInvalidAmount(true);
      return;
    }

    return dispatch(upsertTransaction(params, extraProps)).then(() => {
      resetForm();
      dispatch(loadResume(userId, establishmentId || filterEstablishmentId, { startDate, endDate, qp }));
    });
  }

  return(
    <Formik
      enableReinitialize={transactionId}
      initialValues={{
        ...submit,
        amount: convertToBrl(convertFormattedMoneyToNumber(submit?.amount)),
        accountId: isOnlyOneAccountAvailable && !transactionId
          ? getOnlyAccountAvailable()
          : submit?.accountId
      }}
      validationSchema={paymentValidations}
      onSubmit={handleSavePayment}
    >
      {({ errors, values, handleChange, setFieldValue }) => (
        <ModalDrawerSwitcher
          id="payment-modal"
          title={transactionId ? 'Editar despesa' : 'Adicionar despesa'}
          open={isOpen}
          onClose={handleClosePayment}
        >
          {isAttachmentModalOpen && (
            <Modal
              scroll="body"
              open={isAttachmentModalOpen}
              onClose={() => setIsAttachmentModalOpen(false)}
              title="Comprovante"
            >
              <Grid container justify="center" style={{ padding: 5 }}>
                {isAttachmentError ? (
                  <Typography>
                    Pré visualização não disponível. <a href={attachment} target='_blank'>Clique aqui para fazer o download do {getFileExtensionFromUrl(attachment)}.</a>
                  </Typography>
                ) : (
                  <img
                    src={attachment}
                    width={isSm ? "100%" : "50%"}
                    height="auto"
                    onError={() => setIsAttachmentError(true)}
                  />
                )}
              </Grid>
              <Grid container style={{ marginTop: 10 }}>
                <Grid xs={6} style={{ padding: 5 }}>
                  <Button
                    fullWidth
                    type="button"
                    onClick={() => setIsAttachmentModalOpen(false)}
                  >
                    Voltar
                  </Button>
                </Grid>
                <Grid xs={6} style={{ padding: 5 }}>
                  <Button
                    fullWidth
                    type="button"
                    color="error"
                    disabled={userRestrictions?.includes(+SITE_RESTRICTIONS.REMOVE_TRANSACTION_ATTACHMENT_AND_CASHIER)}
                    loading={attachmentRemoveLoading}
                    onClick={() => {
                      setAttachmentRemoveLoading(true);

                      return setTimeout(() => {
                        toast.warn('Salvar para persistir o comprovante!');

                        dispatch(setAttachment(null));

                        setIsAttachmentModalOpen(false);
                        setAttachmentRemoveLoading(false);
                      }, 1000);
                    }}
                  >
                    Excluir
                  </Button>
                </Grid>
              </Grid>
            </Modal>
          )}
          <Form style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%' }}>
            <div>
              <Loader isLoading={isLoading}>
                <Grid container spacing={1}>
                  <Grid item xs={(isModal && !isMobile) ? 8 : 12}>
                    <Input
                      autoFocus={!isMobile}
                      id="description"
                      name="description"
                      label="Descrição"
                      value={values.description}
                      error={errors.description}
                      helperText={errors.description}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={(isModal && !isMobile) ? 4 : 12}>
                    <Select
                      name="categoryId"
                      label="Categoria"
                      options={paymentCategories}
                      value={parseInt(values.categoryId, 10)}
                      error={errors.categoryId}
                      helperText={errors.categoryId}
                      onChange={e => setFieldValue(e.target.name, e.target.value)}
                    />
                  </Grid>
                </Grid>
                <Grid container spacing={1} alignItems="flex-end">
                  <Grid item xs={(isModal && !isMobile) ? 4 : 12}>
                    <Typography variant="subtitle1" color="primary">
                      Data de vencimento
                    </Typography>
                    {values.transactionId && (
                      <SingleDatePicker
                        name="previsionDateTime"
                        initialDate={values.transactionId ? values.previsionDateTime : moment().format('YYYY-MM-DD HH:mm:ss')}
                        onDateChange={date => setFieldValue('previsionDateTime', convertDateToDB(date))}
                      />
                    )}
                    {!values.transactionId && (
                      <SingleDatePicker
                        name="previsionDateTime"
                        initialDate={values.previsionDateTime}
                        onDateChange={date => setFieldValue('previsionDateTime', convertDateToDB(date))}
                      />
                    )}
                  </Grid>
                  {(cashierId == null) && (
                    <Grid item xs={(isModal && !isMobile) ? 4 : 12}>
                      <Select
                        name="accountId"
                        label="Conta origem"
                        options={availableAccounts}
                        disabled={cashierId}
                        value={parseInt(values.accountId, 10)}
                        error={errors.accountId}
                        helperText={errors.accountId}
                        onChange={e => setFieldValue(e.target.name, e.target.value)}
                      />
                    </Grid>
                  )}
                  <Grid item xs={(isModal && !isMobile) ? 4 : 12}>
                    <Input
                      id="amount"
                      name="amount"
                      label="Valor"
                      type="money"
                      placeholder="R$ 0,00"
                      value={values.amount}
                      error={errors.amount || isInvalidAmount}
                      helperText={errors.amount || (isInvalidAmount && 'O valor precisa ser maior que zero')}
                      onChange={handleChange}
                      maxLength={13}
                    />
                  </Grid>
                </Grid>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Grid container alignItems="center">
                      <FormControlLabel
                        label="Pago"
                        control={
                          <Switch
                            id="paid"
                            name="paid"
                            color="primary"
                            onChange={() => dispatch(handleConfirm(!isConfirm))}
                            checked={isConfirm}
                          />
                        }
                      />
                      {submit?.userName && (
                        <Typography>Transação realizada por: {submit?.userName}</Typography>
                      )}
                    </Grid>
                  </Grid>
                  {rpsNumber > 0 && (
                    <Grid item sm={6} xs={12} style={{ display: 'flex', justifyContent: isMobile ? 'flex-start' : 'flex-end' }}>
                      <p>RPS: {rpsSeries}-{rpsNumber} | N˚ NFSe: {nfNumber}</p>
                    </Grid>
                  )}
                </Grid>
                {isConfirm && (
                  <Grid container>
                    <Grid item xs={12}>
                      <Typography color="primary">Data da despesa</Typography>
                    </Grid>
                    <Grid item xs={(isModal && !isMobile) ? 6 : 12}>
                      {values.transactionId && (
                        <SingleDatePicker
                          name="paymentDateTime"
                          blockFutureDates
                          onBlockedDate={() => toast.error('A data da despesa não pode ser maior do que a data atual.')}
                          initialDate={values.transactionId ? values.paymentDateTime : moment().format('YYYY-MM-DD HH:mm:ss')}
                          onDateChange={date => setFieldValue('paymentDateTime', convertDateToDB(date))}
                        />
                      )}
                      {!values.transactionId && (
                        <SingleDatePicker
                          name="paymentDateTime"
                          blockFutureDates
                          onBlockedDate={() => toast.error('A data da despesa não pode ser maior do que a data atual.')}
                          initialDate={values.paymentDateTime}
                          onDateChange={date => setFieldValue('paymentDateTime', convertDateToDB(date))}
                        />
                      )}
                    </Grid>
                  </Grid>
                )}
              </Loader>
              {isRemoving && (
                <Formik
                  enableReinitialize
                  initialValues={{ reason: '', userPassword: '' }}
                  validationSchema={passwordValidations}
                  onSubmit={handleRemovePayment}
                >
                  {newFormik => (
                    <Form style={{ marginBottom: 10 }}>
                      <Grid container spacing={1}>
                        <Grid item xs={12}>
                          <Typography color="primary" style={{ borderBottom: '1px solid #E4E7EA' }}>
                            <b>Remoção</b>
                          </Typography>
                        </Grid>
                        <Grid item xs={(isModal && !isMobile) ? 6 : 12}>
                          <Input
                            id="reason"
                            type="textarea"
                            label="Observação"
                            placeholder="Digite sua observação"
                            error={newFormik.errors.reason}
                            helperText={newFormik.errors.reason}
                            value={newFormik.values.reason}
                            onChange={newFormik.handleChange}
                          />
                        </Grid>
                        <Grid item xs={(isModal && !isMobile) ? 6 : 12}>
                          <Input
                            id="userPassword"
                            type="password"
                            label="Informe a sua senha"
                            placeholder="Digite sua senha"
                            error={newFormik.errors.userPassword}
                            helperText={newFormik.errors.userPassword}
                            value={newFormik.values.userPassword}
                            onChange={newFormik.handleChange}
                          />
                        </Grid>
                      </Grid>
                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                        <Button
                          type="button"
                          color="error"
                          variant="outlined"
                          loading={isLoading}
                          onClick={() => dispatch(clearChanges())}
                        >
                          Cancelar
                        </Button>
                        <Button
                          type="submit"
                          color="success"
                          loading={isLoading}
                          onClick={event => {
                            event.preventDefault();
                            newFormik.handleSubmit();
                          }}
                        >
                          Confirmar
                        </Button>
                      </div>
                    </Form>
                  )}
                </Formik>
              )}
            </div>
            {!isRemoving && (
              <div style={{ display: transactionId ? 'flex' : 'grid', flexWrap: isMobile ? 'wrap-reverse' : 'nowrap', justifyContent: 'flex-end', gridTemplateColumns: isMobile ? 'repeat(2, 50%)' : 'repeat(4, auto)', gap: 5, padding: 5 }}>
                {transactionId && (
                  <Button
                    type="button"
                    color="error"
                    variant="outlined"
                    startIcon={
                      <FontAwesomeIcon
                        icon={faTrash}
                        color="#E53935"
                        size="xs"
                      />
                    }
                    loading={isLoading}
                    onClick={() => dispatch(setRemove())}
                    disabled={userRestrictions?.includes(SITE_RESTRICTIONS.SINGLE_TRANSACTIONS)}
                  >
                    Remover despesa
                  </Button>
                )}
                {!attachment && (
                  <FileUploader
                    name="attachment"
                    color="primary"
                    startIcon={
                      <FontAwesomeIcon
                        icon={faPlusCircle}
                        color="#FFFFFF"
                        size="xs"
                      />
                    }
                    displayFileName={false}
                    onChange={e => handleUploadFile(e)}
                  >
                    Comprovante
                  </FileUploader>
                )}
                {!!attachment && (
                  <Button
                    type="button"
                    color="primary"
                    onClick={() => setIsAttachmentModalOpen(true)}
                    startIcon={
                      <FontAwesomeIcon
                        icon={faCheck}
                        color="#6DBB7B"
                        size="xs"
                      />
                    }
                  >
                    Comprovante
                  </Button>
                )}
                <Button
                  color="success"
                  loading={isLoading}
                >
                  Salvar
                </Button>
              </div>
            )}
          </Form>
        </ModalDrawerSwitcher>
      )}
    </Formik>
  );
}

Payment.propTypes = {
  isOpen: PropTypes.bool.isRequired
};